/**
 * Identity Context - Web Agent Identity Management
 *
 * Implements:
 * - REQ-011: Agent Identity Persistence (Web)
 * - ADR-007: Browser Key Storage
 *
 * Stores Ed25519 keypair in IndexedDB.
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { get, set } from "idb-keyval";
import * as ed from "@noble/ed25519";

interface Identity {
  privateKey: Uint8Array;
  publicKey: Uint8Array;
  createdAt: string;
}

interface IdentityContextValue {
  privateKey: Uint8Array | null;
  publicKey: string | null;
  isLoading: boolean;
  sign: (payload: unknown) => Promise<string>;
}

const IdentityContext = createContext<IdentityContextValue | null>(null);

function base64urlEncode(bytes: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...bytes));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function canonicalize(obj: unknown): string {
  return JSON.stringify(obj, Object.keys(obj as object).sort());
}

export function IdentityProvider({ children }: { children: ReactNode }) {
  const [privateKey, setPrivateKey] = useState<Uint8Array | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadOrCreateIdentity() {
      try {
        let identity = await get<Identity>("hence-identity");

        if (!identity) {
          // Generate new keypair
          const privKey = ed.utils.randomPrivateKey();
          const pubKey = await ed.getPublicKeyAsync(privKey);

          identity = {
            privateKey: privKey,
            publicKey: pubKey,
            createdAt: new Date().toISOString(),
          };

          await set("hence-identity", identity);
        }

        setPrivateKey(identity.privateKey);
        setPublicKey(base64urlEncode(identity.publicKey));
      } catch (err) {
        console.error("Failed to load/create identity:", err);
      } finally {
        setIsLoading(false);
      }
    }

    loadOrCreateIdentity();
  }, []);

  async function sign(payload: unknown): Promise<string> {
    if (!privateKey) {
      throw new Error("No private key available");
    }
    const message = new TextEncoder().encode(canonicalize(payload));
    const signature = await ed.signAsync(message, privateKey);
    return base64urlEncode(signature);
  }

  return (
    <IdentityContext.Provider value={{ privateKey, publicKey, isLoading, sign }}>
      {children}
    </IdentityContext.Provider>
  );
}

export function useIdentity() {
  const context = useContext(IdentityContext);
  if (!context) {
    throw new Error("useIdentity must be used within IdentityProvider");
  }
  return context;
}
